import IOfferDefaults, {
  IOfferSigner,
  OfferDefaultsKind,
} from 'api/interfaces/offers/IOfferDefaults';
import IOfferChoice, {
  IOfferChoicePost,
} from 'api/interfaces/offers/IOfferChoice';
import IOfferInfoRow from 'api/interfaces/offers/IOfferInfoRow';
import IPriceListTemplate from 'api/interfaces/offers/IPriceListTemplate';
import IOfferGet from 'api/interfaces/offers/IOfferGet';
import IOfferGetSingle from 'api/interfaces/offers/IOfferGetSingle';
import IOfferVisit from 'api/interfaces/offers/IOfferVisit';
import api from './api';
import IOfferProductPost from './interfaces/offers/IOfferProductPost';
import IOfferProductGet from './interfaces/offers/IOfferProductGet';
import IOfferProductsGet from './interfaces/offers/IOfferProductsGet';
import IIdResult from './interfaces/results/IIdResult';
import ISuccessResult from './interfaces/results/ISuccessResult';
import IAdditionalOfferGet from './interfaces/offers/IAdditionalOfferGet';
import IOfferProductImage from './interfaces/offers/IOfferProductImage';
import { IOfferNdaPut } from './interfaces/offers/IOfferNda';
import IOfferRevision from './interfaces/offers/IOfferRevision';
import IOfferTextTemplate from './interfaces/offers/IOfferTextTemplate';
import ISearchedPosition from './interfaces/offers/ISearchedPosition';
import IPdfLayoutPosition from './interfaces/offers/IPdfLayoutPosition';
import IRestorablePosition from './interfaces/offers/IRestorablePosition';

// The printing of the additional price list: a missing value is kept, the text is changed
// only with setText (an empty text removes it)
export interface IPriceListSettings {
  setText?: boolean;
  showQuantities?: boolean;
  text?: string;
}

export default {
  additionalOffers: {
    delete: (id: number): Promise<ISuccessResult> =>
      api.delete(`/api/v1/additionaloffers/${id}`),
    get: async (id?: number): Promise<IAdditionalOfferGet> =>
      api.get(`/api/v1/additionaloffers/${id}`),
    pdfs: {
      get: async (id?: number): Promise<any> =>
        api.getPdf(`/api/v1/additionaloffers/${id}/pdf`),
    },
    post: async (offer?: any): Promise<any> =>
      api.post('/api/v1/additionaloffers', offer),
    postCopyAdditionalOffer: async (id?: number): Promise<any> =>
      api.post(`/api/v1/copyadditionaloffer/${id}`),
    put: async (offer?: any, id?: number): Promise<any> =>
      api.put(`/api/v1/additionaloffers/${id}`, offer),
    patchStatus: (id: number, status: string): Promise<IIdResult> =>
      api.patch(`/api/v1/additionaloffers/${id}/status`, { status }),
    patchMemo: (id: number, memo: string): Promise<IIdResult> =>
      api.patch(`/api/v1/additionaloffers/${id}/memo`, { memo }),
    // How the additional price list is printed
    patchPriceListSettings: (
      id: number,
      settings: IPriceListSettings
    ): Promise<IIdResult> =>
      api.patch(`/api/v1/additionaloffers/${id}/price-list-settings`, settings),
    // The order the user has given to the positions (within their work types)
    positionOrder: {
      put: (id: number, offerProductIds: number[]): Promise<IIdResult> =>
        api.put(`/api/v1/additionaloffers/${id}/position-order`, {
          offerProductIds,
        }),
    },
    copiedPositions: {
      post: (id: number, offerProductId: number): Promise<IIdResult> =>
        api.post(`/api/v1/additionaloffers/${id}/copied-positions`, {
          offerProductId,
        }),
    },
    pdfLayout: {
      get: (id: number): Promise<IPdfLayoutPosition[]> =>
        api.get(`/api/v1/additionaloffers/${id}/pdf-layout`),
    },
    revisions: {
      get: (id: number): Promise<IOfferRevision[]> =>
        api.get(`/api/v1/additionaloffers/${id}/revisions`),
      post: (id: number): Promise<IIdResult> =>
        api.post(`/api/v1/additionaloffers/${id}/revisions`),
    },
    restorablePositions: {
      get: (id: number): Promise<IRestorablePosition[]> =>
        api.get(`/api/v1/additionaloffers/${id}/restorable-positions`),
      restore: (id: number, offerProductId: number): Promise<IIdResult> =>
        api.post(`/api/v1/additionaloffers/${id}/restored-positions`, {
          offerProductId,
        }),
    },
  },
  count: async (filters: {
    created?: string;
    createdUserId?: string;
    customer?: string;
    // Departments (organization unit ids) separated by commas
    organizationUnitId?: string;
    recipient?: string;
    search?: string;
    orderBy?: string;
    orderByDirection?: string;
    projectId?: number;
    updated?: string;
    updatedUserId?: string;
    contactId?: string;
    type?: string;
    status?: string;
  }): Promise<any> => api.get('/api/v1/offers/count', filters),
  get: async (filters: {
    created?: string;
    createdUserId?: string;
    customer?: string;
    // Departments (organization unit ids) separated by commas
    organizationUnitId?: string;
    recipient?: string;
    limit?: number;
    offset?: number;
    search?: string;
    orderBy?: string;
    orderByDirection?: string;
    projectId?: number;
    updated?: string;
    updatedUserId?: string;
    contactId?: string;
    type?: string;
    status?: string;
  }): Promise<IOfferGet[]> => api.get('/api/v1/offers', filters),
  // The departments whose offers the user can see, for the offer list. With writable
  // only the departments whose offers the user can edit, with all every department (a
  // sales opportunity can be given to any department).
  departments: {
    get: async (
      writable?: boolean,
      all?: boolean
    ): Promise<{ id: number; name: string }[]> =>
      api.get(
        '/api/v1/offers/departments',
        writable || all
          ? {
              ...(writable ? { writable: true } : {}),
              ...(all ? { all: true } : {}),
            }
          : undefined
      ),
  },
  // The choices of the dropdown lists of the offers, managed by the admins; the hidden ones
  // too, as the offers that have one still show it
  choices: {
    get: (): Promise<IOfferChoice[]> => api.get('/api/v1/offers/choices'),
    order: (list: string, ids: number[]): Promise<number> =>
      api.put('/api/v1/offers/choices/order', { ids, list }),
    post: (choice: IOfferChoicePost): Promise<IIdResult> =>
      api.post('/api/v1/offers/choices', choice),
    put: (id: number, choice: IOfferChoicePost): Promise<IIdResult> =>
      api.put(`/api/v1/offers/choices/${id}`, choice),
  },
  // The basic information the new offers of a department get; without a department, of
  // the user's own. For a new offer those of the current user first, then those of the
  // department; exact: those saved for exactly the kind and the user (none: department).
  defaults: {
    get: (
      query: {
        exact?: boolean;
        kind?: OfferDefaultsKind;
        organizationUnitId?: number;
        userId?: null | string;
      } = {}
    ): Promise<IOfferDefaults> =>
      api.get('/api/v1/offers/defaults', {
        ...(query.exact ? { exact: true } : {}),
        ...(query.kind ? { kind: query.kind } : {}),
        ...(query.organizationUnitId === undefined
          ? {}
          : { organizationUnitId: query.organizationUnitId }),
        ...(query.userId ? { userId: query.userId } : {}),
      }),
    delete: (
      organizationUnitId: number,
      kind: OfferDefaultsKind,
      userId: null | string
    ): Promise<boolean> =>
      api.delete(
        `/api/v1/offers/defaults/${organizationUnitId}?kind=${kind}${
          userId ? `&userId=${encodeURIComponent(userId)}` : ''
        }`
      ),
    put: (
      organizationUnitId: number,
      defaults: {
        kind: OfferDefaultsKind;
        userId: null | string;
        attachmentsNote: string;
        cargo: string;
        collateralType: null | string;
        country: null | string;
        deliveryTermsTemplateId: null | number;
        deliveryTimeNote: string;
        infoRows: IOfferInfoRow[];
        invoiceNote: string;
        offerTextTemplateId: null | number;
        paymentTerm: null | number;
        priceListTemplateId: null | number;
        promoTextEn: string;
        promoTextFi: string;
        promoTextSv: string;
        signatureUserId: null | string;
        validity: string;
      }
    ): Promise<IIdResult> =>
      api.put(`/api/v1/offers/defaults/${organizationUnitId}`, defaults),
    // A user as printed below the signature of the PDF
    getSigner: (userId: string): Promise<IOfferSigner> =>
      api.get(`/api/v1/offers/signers/${userId}`),
  },
  // Moves the offer with its revisions and additional offers to another department
  patchOrganizationUnit: (
    id: number,
    organizationUnitId: number
  ): Promise<IIdResult> =>
    api.patch(`/api/v1/offers/${id}/organization-unit`, {
      organizationUnitId,
    }),
  getOffer: async (id?: number): Promise<IOfferGetSingle> =>
    api.get(`/api/v1/offers/${id}`),
  offerTypes: {
    get: async (): Promise<string[]> => api.get('/api/v1/offers/types'),
  },
  pdfs: {
    // recipientId prints the version of the offer for one recipient
    get: async (id?: number, recipientId?: number): Promise<any> =>
      api.getPdf(
        `/api/v1/offers/${id}/pdf`,
        recipientId === undefined ? undefined : { recipientId }
      ),
  },
  post: async (offer?: any): Promise<any> => api.post('/api/v1/offers', offer),
  postCopyOffer: async (id?: number): Promise<any> =>
    api.post(`/api/v1/copyoffer/${id}`),
  delete: (id: number): Promise<ISuccessResult> =>
    api.delete(`/api/v1/offers/${id}`),
  patchStatus: (id: number, status: string): Promise<IIdResult> =>
    api.patch(`/api/v1/offers/${id}/status`, { status }),
  patchMemo: (id: number, memo: string): Promise<IIdResult> =>
    api.patch(`/api/v1/offers/${id}/memo`, { memo }),
  // How the additional price list is printed
  patchPriceListSettings: (
    id: number,
    settings: IPriceListSettings
  ): Promise<IIdResult> =>
    api.patch(`/api/v1/offers/${id}/price-list-settings`, settings),
  // NDA protected parts and users, shared by all revisions and additional offers
  putNda: (id: number, nda: IOfferNdaPut): Promise<IIdResult> =>
    api.put(`/api/v1/offers/${id}/nda`, nda),
  // Positions of earlier offers whose content can be copied to a new position
  positionSearch: {
    get: (filters: {
      // The offer being worked on (or the offer of the position): its positions are
      // found also when it is not the listed revision of its chain
      currentAdditionalOfferId?: number;
      currentOfferId?: number;
      currentOfferProductId?: number;
      // A position not to find, e.g. the one the reference prices are shown for
      excludeOfferProductId?: number;
      group?: string;
      // The exact position name and the offers of the last months, for reference prices
      months?: number;
      name?: string;
      // Only the offers of the department
      organizationUnitId?: number;
      search?: string;
      status?: string;
      type?: string;
    }): Promise<ISearchedPosition[]> =>
      api.get('/api/v1/offers/position-search', filters),
  },
  copiedPositions: {
    post: (id: number, offerProductId: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/copied-positions`, { offerProductId }),
  },
  // A new material offer of the suspended ceilings of the offer, which stays as it is
  materialOffers: {
    post: (id: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/material-offers`, {}),
  },
  // The order the user has given to the positions (within their work types)
  positionOrder: {
    put: (id: number, offerProductIds: number[]): Promise<IIdResult> =>
      api.put(`/api/v1/offers/${id}/position-order`, { offerProductIds }),
  },
  // Archive of reusable offer texts
  textTemplates: {
    delete: (id: number): Promise<ISuccessResult> =>
      api.delete(`/api/v1/offers/text-templates/${id}`),
    // The type is the kind of the texts, e.g. delivery-terms; offer texts without it
    get: (search?: string, type?: string): Promise<IOfferTextTemplate[]> =>
      api.get('/api/v1/offers/text-templates', {
        ...(search ? { search } : {}),
        ...(type ? { type } : {}),
      }),
    post: (name: string, text: string, type?: string): Promise<IIdResult> =>
      api.post('/api/v1/offers/text-templates', { name, text, type }),
  },
  // The registry of additional price lists, whose rows a new offer of a department can get
  priceListTemplates: {
    delete: (id: number): Promise<boolean> =>
      api.delete(`/api/v1/offers/price-list-templates/${id}`),
    get: (): Promise<IPriceListTemplate[]> =>
      api.get('/api/v1/offers/price-list-templates'),
    // The price list of an offer or additional offer, as a new list or over an own one
    post: (save: {
      additionalOfferId?: number;
      name: string;
      offerId?: number;
      templateId: null | number;
    }): Promise<IIdResult> =>
      api.post('/api/v1/offers/price-list-templates', save),
  },
  pdfLayout: {
    get: (id: number): Promise<IPdfLayoutPosition[]> =>
      api.get(`/api/v1/offers/${id}/pdf-layout`),
  },
  revisions: {
    get: (id: number): Promise<IOfferRevision[]> =>
      api.get(`/api/v1/offers/${id}/revisions`),
    post: (id: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/revisions`),
  },
  restorablePositions: {
    get: (id: number): Promise<IRestorablePosition[]> =>
      api.get(`/api/v1/offers/${id}/restorable-positions`),
    restore: (id: number, offerProductId: number): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${id}/restored-positions`, { offerProductId }),
  },

  // An open offer page or position calculation reports itself and gets the other users
  // on it now
  visits: {
    put: (visit: {
      additionalOfferId?: number;
      editing: boolean;
      offerId?: number;
      offerProductId?: number;
      visitId?: number;
    }): Promise<IOfferVisit> => api.put('/api/v1/offers/visits', visit),
    close: (visitId: number): Promise<boolean> =>
      api.put(`/api/v1/offers/visits/${visitId}/close`, {}),
  },
  products: {
    delete: (id: number): Promise<ISuccessResult> =>
      api.delete(`/api/v1/offers/products/${id}`),
    // The position code (e.g. VS1) changed in the summary of the offer page
    patchPosition: (productId: number, position: string): Promise<IIdResult> =>
      api.patch(`/api/v1/offers/products/${productId}/position`, { position }),
    patchPageBreak: (
      productId: number,
      pageBreakBefore: boolean
    ): Promise<IIdResult> =>
      api.patch(`/api/v1/offers/products/${productId}/page-break`, {
        pageBreakBefore,
      }),
    images: {
      delete: (productId: number, imageId: number): Promise<ISuccessResult> =>
        api.delete(`/api/v1/offers/products/${productId}/images/${imageId}`),
      // versionId returns the images of one alternative of the position
      get: (
        productId: number,
        versionId?: number
      ): Promise<IOfferProductImage[]> =>
        api.get(
          `/api/v1/offers/products/${productId}/images`,
          versionId === undefined ? undefined : { versionId }
        ),
      getFile: (productId: number, imageId: number): Promise<any> =>
        api.getFile(
          `/api/v1/offers/products/${productId}/images/${imageId}/file`
        ),
      patch: (
        productId: number,
        imageId: number,
        height: string,
        caption: null | string
      ): Promise<IIdResult> =>
        api.patch(`/api/v1/offers/products/${productId}/images/${imageId}`, {
          caption,
          height,
        }),
      // The image belongs to an alternative (calculation version) of the position
      post: (
        productId: number,
        versionId: number,
        fileId: string,
        height: string,
        caption: null | string
      ): Promise<IIdResult> =>
        api.post(`/api/v1/offers/products/${productId}/images`, {
          caption,
          fileId,
          height,
          versionId,
        }),
    },
    get: (id: number): Promise<IOfferProductGet> =>
      api.get(`/api/v1/offers/products/${id}`),
    // The same position on the preliminary offer and the other versions
    history: (
      id: number
    ): Promise<
      {
        isPreliminaryOffer: boolean;
        offerProductId: number;
        revisionNumber: number;
      }[]
    > => api.get(`/api/v1/offers/products/${id}/history`),
    getForAdditionalOffer: (offerId: number): Promise<IOfferProductsGet[]> =>
      api.get(`/api/v1/additionaloffers/${offerId}/products`),
    getForOffer: (offerId: number): Promise<IOfferProductsGet[]> =>
      api.get(`/api/v1/offers/${offerId}/products`),
    post: async (
      offerId: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.post(`/api/v1/offers/${offerId}/products`, offerProduct),
    postAdditionalOffer: async (
      offerId: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.post(`/api/v1/additionaloffers/${offerId}/products`, offerProduct),
    put: async (
      id: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.put(`/api/v1/offers/products/${id}`, offerProduct),
    putAdditionalOffer: async (
      id: number,
      offerProduct: IOfferProductPost
    ): Promise<IIdResult> =>
      api.put(`/api/v1/offers/products/${id}`, offerProduct),
  },
  put: async (offer?: any, id?: number): Promise<any> =>
    api.put(`/api/v1/offers/${id}`, offer),
  status: {
    get: async (): Promise<string[]> => api.get('/api/v1/offers/types/status'),
  },
};
